import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { selectIsWaitingForConf } from '../../slices/appState';
import { useSelector } from 'react-redux';
/**
 * Props:
 * - strategy?: 'auto' | 'inline' | 'link' | 'both' (default: 'auto')
 * - onReady?: () => void
 */
export default function FontHandler({ strategy = 'auto', onReady }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isWatingForConf = useSelector(selectIsWaitingForConf);

  const faces = useMemo(() => deepCollectFontFaces(theme), [theme]);
  const urls = useMemo(() => deepCollectFontUrls(theme), [theme]);

  // Decide which loaders to run
  const { useInline, useLink } = useMemo(() => {
    switch (strategy) {
      case 'inline':
        return { useInline: faces.length > 0, useLink: false };
      case 'link':
        return { useInline: false, useLink: urls.length > 0 };
      case 'both':
        return { useInline: faces.length > 0, useLink: urls.length > 0 };
      case 'auto':
      default:
        // Prefer inline if faces exist; otherwise fall back to link URLs
        if (faces.length > 0) return { useInline: true, useLink: false };
        return { useInline: false, useLink: urls.length > 0 };
    }
  }, [strategy, faces.length, urls.length]);

  const expectedLoaders = (useInline ? 1 : 0) + (useLink ? 1 : 0);
  const [doneCount, setDoneCount] = useState(0);
  const genRef = useRef(0);

  // Reset loading tracking when inputs change
  useEffect(() => {
    genRef.current += 1;
    setDoneCount(0);
    if (expectedLoaders === 0) {
      // Nothing to load
      onReady?.();
    }
  }, [expectedLoaders, dispatch, onReady, strategy, faces, urls]);

  // Mark completion and signal onReady when all chosen loaders finish
  const handlePartReady = useCallback(() => {
    setDoneCount((c) => c + 1);
  }, []);

  useEffect(() => {
    if (expectedLoaders > 0 && doneCount >= expectedLoaders) {
      onReady?.();
    }
  }, [doneCount, expectedLoaders, dispatch, onReady]);

  if (isWatingForConf) {
    return;
  }

  return (
    <>
      {useInline && <FontFaceLoader faces={faces} onReady={handlePartReady} />}
      {useLink && <FontLinkLoader urls={urls} onReady={handlePartReady} />}
    </>
  );
}

/**
 * Props:
 * - faces: Array<{
 *     family: string,
 *     src: Array<{url?: string, format?: string} | {local: string} | string>,
 *     weight?: string|number, style?: string, stretch?: string,
 *     display?: string, unicodeRange?: string
 *   }>
 * - onReady: () => void
 */
function FontFaceLoader({ faces = [], onReady }) {
  const styleElRef = useRef(null);
  const genRef = useRef(0);

  useEffect(() => {
    const head = document?.head;
    const gen = ++genRef.current;

    if (!head) {
      onReady?.();
      return;
    }

    // If nothing to load, resolve immediately
    if (!faces.length) {
      onReady?.();
      return;
    }

    let styleEl = styleElRef.current;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.type = 'text/css';
      styleEl.setAttribute('data-font-inline', 'true');
      styleElRef.current = styleEl;
      head.appendChild(styleEl);
    }

    styleEl.textContent = faces.map(faceToCss).join('\n');

    let cancelled = false;
    Promise.resolve()
      .then(() => waitForFaces(faces))
      .catch(() => {})
      .finally(() => {
        if (!cancelled && gen === genRef.current) onReady?.();
      });

    return () => {
      cancelled = true;
    };
  }, [faces, onReady]);

  // Cleanup style tag on unmount
  useEffect(() => {
    return () => {
      const styleEl = styleElRef.current;
      if (styleEl && styleEl.parentNode) {
        styleEl.parentNode.removeChild(styleEl);
        styleElRef.current = null;
      }
    };
  }, []);

  return null;
}

/**
 * Props:
 * - urls: string[] (CSS stylesheet URLs)
 * - onReady: () => void
 */
function FontLinkLoader({ urls = [], onReady }) {
  const instanceIdRef = useRef(`fll-${Math.random().toString(36).slice(2)}`);
  const prevUrlsRef = useRef([]);

  useEffect(() => {
    const head = document?.head;
    if (!head) {
      onReady?.();
      return;
    }

    // If nothing to load, resolve immediately
    if (!urls.length) {
      onReady?.();
      return;
    }

    const instanceId = instanceIdRef.current;

    const findExistingLink = (href) =>
      head.querySelector(`link[rel="stylesheet"][href="${href}"]`);

    // Add new links
    for (const href of urls) {
      let link = findExistingLink(href);
      if (!link) {
        link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.setAttribute('data-font-css', 'true');
        link.setAttribute('data-font-css-instance', instanceId);
        head.appendChild(link);
      } else if (!link.hasAttribute('data-font-css')) {
        link.setAttribute('data-font-css', 'external');
      }
    }

    // Remove links previously added by this instance but no longer referenced
    const prev = prevUrlsRef.current;
    const toRemove = prev.filter((u) => !urls.includes(u));
    for (const href of toRemove) {
      const link = head.querySelector(
        `link[rel="stylesheet"][data-font-css-instance="${instanceId}"][href="${href}"]`,
      );
      if (link) head.removeChild(link);
    }
    prevUrlsRef.current = urls;

    const allLinks = urls
      .map((href) =>
        head.querySelector(`link[rel="stylesheet"][href="${href}"]`),
      )
      .filter(Boolean);

    let cancelled = false;
    Promise.all(allLinks.map((l) => waitForStylesheet(l))).finally(() => {
      if (!cancelled) onReady?.();
    });

    return () => {
      cancelled = true;
    };
  }, [urls, onReady]);

  return null;
}

/************************  Helpers **************************/

function quoteFamily(name) {
  const safe = String(name).replace(/"/g, '\\"');
  return `"${safe}"`;
}
function buildSrc(srcArray = []) {
  const parts = [];
  for (const item of srcArray) {
    if (!item) continue;
    if (typeof item === 'string') {
      parts.push(item.trim());
      continue;
    }
    if (item.local) {
      const localName = String(item.local).replace(/"/g, '\\"');
      parts.push(`local("${localName}")`);
    } else if (item.url) {
      const fmt = item.format ? ` format("${item.format}")` : '';
      const url = String(item.url).replace(/"/g, '\\"');
      parts.push(`url("${url}")${fmt}`);
    }
  }
  return parts.join(', ');
}
function faceToCss(face) {
  const fam = quoteFamily(face.family);
  const src = buildSrc(face.src);
  let css = '@font-face{';
  css += `font-family:${fam};`;
  css += `font-style:${face.style || 'normal'};`;
  css += `font-weight:${face.weight ?? '400'};`;
  css += `font-display:${face.display || 'swap'};`;
  css += `font-stretch:${face.stretch || 'normal'};`;
  css += `src:${src};`;
  if (face.unicodeRange) css += `unicode-range:${face.unicodeRange};`;
  css += '}';
  return css;
}
function pickProbeWeight(weight) {
  if (typeof weight === 'number') return weight;
  if (typeof weight === 'string') {
    const match = weight.match(/\d{2,3}/);
    if (match) return parseInt(match[0], 10);
  }
  return 400;
}

async function waitForFaces(faces) {
  if (!('fonts' in document) || typeof document.fonts.load !== 'function')
    return;
  const loads = faces.map((f) => {
    const w = pickProbeWeight(f.weight);
    const fam = quoteFamily(f.family);
    const style = f.style || 'normal';
    return document.fonts.load(`${style} ${w} 1em ${fam}`);
  });
  await Promise.allSettled(loads);
}

function waitForStylesheet(link) {
  return new Promise((resolve) => {
    if (link.sheet) {
      resolve();
      return;
    }
    const onDone = () => {
      link.removeEventListener('load', onDone);
      link.removeEventListener('error', onDone);
      resolve();
    };
    link.addEventListener('load', onDone, { once: true });
    link.addEventListener('error', onDone, { once: true });
  });
}

function isObjectLike(val) {
  return val !== null && typeof val === 'object';
}

// Collect CSS URLs from theme.fontSource (string or string[])
function deepCollectFontUrls(root) {
  const urls = [];
  const visited = new Set();
  const stack = [root];
  while (stack.length) {
    const node = stack.pop();
    if (!isObjectLike(node)) continue;
    if (visited.has(node)) continue;
    visited.add(node);

    if (Array.isArray(node)) {
      for (const item of node) stack.push(item);
      continue;
    }

    for (const [key, value] of Object.entries(node)) {
      if (key === 'fontSource') {
        if (typeof value === 'string' && value.trim()) {
          urls.push(value.trim());
        } else if (Array.isArray(value)) {
          for (const v of value) {
            if (typeof v === 'string' && v.trim()) urls.push(v.trim());
          }
        }
      } else if (isObjectLike(value)) {
        stack.push(value);
      }
    }
  }
  const seen = new Set();
  const unique = [];
  for (const u of urls) {
    if (!seen.has(u)) {
      seen.add(u);
      unique.push(u);
    }
  }
  return unique;
}

// Collect inline @font-face descriptors from theme (fontFace or fontFaces)
function normalizeFace(face) {
  if (!face || !face.family || !face.src) return null;
  return {
    family: String(face.family),
    style: face.style || 'normal',
    weight: face.weight ?? '400',
    display: face.display || 'swap',
    stretch: face.stretch || 'normal',
    unicodeRange: face.unicodeRange || undefined,
    src: Array.isArray(face.src) ? face.src : [face.src],
  };
}
function faceKey(face) {
  const srcKey = JSON.stringify(face.src);
  return [
    face.family,
    face.style,
    String(face.weight),
    face.stretch,
    face.display || 'swap',
    face.unicodeRange || '',
    srcKey,
  ].join('|');
}
function collectFacesFromNode(value, out) {
  if (!isObjectLike(value)) return;
  for (const [key, val] of Object.entries(value)) {
    if (key === 'fontFace' || key === 'fontFaces') {
      const add = (v) => {
        const n = normalizeFace(v);
        if (n) out.push(n);
      };
      if (Array.isArray(val)) {
        for (const v of val) add(v);
      } else if (isObjectLike(val)) {
        add(val);
      }
    } else if (isObjectLike(val)) {
      collectFacesFromNode(val, out);
    }
  }
}
function deepCollectFontFaces(root) {
  const faces = [];
  const visited = new Set();
  const stack = [root];
  while (stack.length) {
    const node = stack.pop();
    if (!isObjectLike(node)) continue;
    if (visited.has(node)) continue;
    visited.add(node);

    collectFacesFromNode(node, faces);

    if (Array.isArray(node)) {
      for (const item of node) stack.push(item);
    } else {
      for (const v of Object.values(node)) {
        if (isObjectLike(v)) stack.push(v);
      }
    }
  }
  const seen = new Set();
  const unique = [];
  for (const f of faces) {
    const k = faceKey(f);
    if (!seen.has(k)) {
      seen.add(k);
      unique.push(f);
    }
  }
  return unique;
}
